import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import type { Tone } from '@/components/ui/StatusPill'

export interface StatSegment {
  key: string
  label: string
  value: number | string
  hint?: ReactNode
  tone?: Tone
  /** Pulses: the number is moving on its own. */
  live?: boolean
}

const BAR: Record<Tone, string> = {
  neutral: 'bg-ink/30',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  brand: 'bg-brand',
}
const TEXT: Record<Tone, string> = {
  neutral: 'text-ink',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
  brand: 'text-brand',
}

/**
 * Several counts in one strip: a proportional bar on top, the numbers below.
 * Replaces a row of identical stat cards; reads in one glance and fits a
 * phone in one row. Each segment is a button when `onSelect` is given.
 */
export function StatStrip({
  segments,
  active,
  onSelect,
  className,
  loading,
}: {
  segments: StatSegment[]
  active?: string
  onSelect?: (key: string) => void
  className?: string
  loading?: boolean
}) {
  const numeric = segments.map((s) => (typeof s.value === 'number' ? s.value : 0))
  const total = numeric.reduce((sum, n) => sum + n, 0)

  return (
    <div className={cn('panel overflow-hidden', className)}>
      <div className="flex h-1.5 w-full gap-px bg-ink/[0.05]" aria-hidden="true">
        {total > 0 &&
          segments.map((segment, index) => {
            const n = numeric[index] ?? 0
            return n > 0 ? (
              <span
                key={segment.key}
                className={cn('h-full transition-[width] duration-500', BAR[segment.tone ?? 'neutral'])}
                style={{ width: `${(n / total) * 100}%` }}
              />
            ) : null
          })}
      </div>
      <div
        className={cn(
          'grid divide-x divide-line',
          segments.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3 sm:grid-cols-6',
        )}
      >
        {segments.map((segment) => {
          const selected = active === segment.key
          const tone = segment.tone ?? 'neutral'
          const content = (
            <>
              <span className="flex items-center gap-1.5 text-[12px] font-medium text-ink-muted">
                <span className="relative flex h-1.5 w-1.5">
                  {segment.live && <span className={cn('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', BAR[tone])} />}
                  <span className={cn('relative inline-flex h-1.5 w-1.5 rounded-full', BAR[tone])} />
                </span>
                {segment.label}
              </span>
              <span className={cn('num mt-1 block text-[24px] font-semibold leading-none', loading ? 'text-ink-faint' : TEXT[tone])}>
                {loading ? '–' : segment.value}
              </span>
              {segment.hint && <span className="mt-1 block truncate text-[11.5px] text-ink-faint">{segment.hint}</span>}
            </>
          )
          const base = cn(
            'min-w-0 px-4 py-3 text-left transition-colors sm:px-5',
            selected && 'bg-accent-soft',
          )
          return onSelect ? (
            <button
              key={segment.key}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelect(segment.key)}
              className={cn(base, 'hover:bg-surface-muted')}
            >
              {content}
            </button>
          ) : (
            <div key={segment.key} className={base}>
              {content}
            </div>
          )
        })}
      </div>
    </div>
  )
}
